/**
 * The numbers a prompt arrived with, and ours, kept visibly apart.
 *
 * This is the display half of the V6 fix. The card previously rendered `★ 0.0 · ⬇ 0` on every
 * tile — unconditionally, from our own counters, which are zero for essentially the whole
 * library. So the screen was not merely missing the real numbers; it was actively showing a
 * prompt with 2,913 Civitai reactions as if nobody had ever wanted it. A `0` is a review. An
 * absent number just reads as new.
 *
 * Two rules hold everywhere below:
 *
 * - **A source number always carries its platform.** "2.9k" is a claim about us; "2.9k · Civitai"
 *   is a fact about somewhere else. The platform is never dropped for space — the metric is
 *   dropped first, then the whole line, but never the attribution.
 * - **Our numbers and their numbers are never adjacent, never summed.** They measure different
 *   populations and merging them would invent a figure that is true of nothing.
 *
 * Visually this is secondary information: hint colour, caption size, tabular numerals so digits
 * don't jitter between cards in a grid. It sits under the title and never competes with the art.
 */

import type { SourceEngagement, SourceMetricKey } from '@promptvault/shared';
import { compactNumber } from '../lib/format';
import { useI18n } from '../lib/i18n';
import type { Lang } from '../lib/i18n';
import { Icon, type IconName } from './Icon';

/**
 * One icon per metric. `heart` covers likes/hearts/reactions because they are the same gesture
 * under three platforms' names, and a distinct glyph each would imply a distinction readers do
 * not have.
 */
const METRIC_ICON: Record<string, IconName> = {
  likes: 'heart',
  hearts: 'heart',
  reactions: 'heart',
  stars: 'star',
  views: 'eye',
  comments: 'chat',
  downloads: 'download',
  uses: 'sparkle',
  shares: 'share',
};

const METRIC_LABEL_EN: Record<string, string> = {
  likes: 'likes',
  hearts: 'hearts',
  reactions: 'reactions',
  stars: 'stars',
  views: 'views',
  comments: 'comments',
  downloads: 'downloads',
  uses: 'uses',
  shares: 'shares',
};

/**
 * Farsi metric names. The bot shipped `❤️ ۵۸۶ likes در youmind.com` once — Persian digits and a
 * Persian preposition around an English noun — so the same vocabulary is translated here rather
 * than left to leak through a second time.
 */
const METRIC_LABEL_FA: Record<string, string> = {
  likes: 'پسند',
  hearts: 'قلب',
  reactions: 'واکنش',
  stars: 'ستاره',
  views: 'بازدید',
  comments: 'دیدگاه',
  downloads: 'دانلود',
  uses: 'استفاده',
  shares: 'هم‌رسانی',
};

/** Falls back to the raw key, so a metric we have not been asked to translate still reads true. */
export function metricLabel(key: SourceMetricKey, lang: Lang): string {
  return (lang === 'fa' ? METRIC_LABEL_FA[key] : METRIC_LABEL_EN[key]) ?? key;
}

/** `sparkle` is the neutral stand-in for a metric this build has never seen. */
function iconFor(key: SourceMetricKey): IconName {
  return METRIC_ICON[key] ?? 'sparkle';
}

/**
 * How much of our own activity is worth reporting.
 *
 * `> 0` is the wrong test. The first verification run put `⬇ 1` on the featured card — a download
 * this session had just created by tapping the button — and one download is not a fact about
 * popularity, it is a fact about testing. Displaying it is barely better than displaying `0`.
 *
 * Ten is where a count stops being explainable by the author plus a couple of people who
 * wandered past. Ratings need a separate, lower bar for a different reason: the number shown is
 * an *average*, and an average of one vote is not a rating at all.
 */
export const MEANINGFUL_COUNT = 10;
export const MEANINGFUL_RATINGS = 3;

/** Whether our own numbers say anything yet. Shared so card and detail agree. */
export function hasInternalSignal(c: {
  downloadCount: number;
  ratingCount: number;
  favoriteCount?: number;
}): boolean {
  return (
    c.downloadCount >= MEANINGFUL_COUNT ||
    c.ratingCount >= MEANINGFUL_RATINGS ||
    (c.favoriteCount ?? 0) >= MEANINGFUL_COUNT
  );
}

/**
 * The compact line: one metric, then the platform.
 *
 * One number, because a grid tile that lists four metrics stops being scannable — the eye should
 * take the whole card in at once. Which metric is the headline is decided server-side per
 * platform, so a Civitai card leads with reactions and a GitHub card with stars.
 */
export function SourceStatLine({
  engagement,
  className = '',
}: {
  engagement: SourceEngagement | null;
  className?: string;
}) {
  const { lang } = useI18n();
  if (!engagement) return null;

  const { primary, label } = engagement;
  return (
    <span
      className={`tnum flex min-w-0 items-center gap-1 text-caption text-hint ${className}`}
      // The whole line is one bidi run: a Latin platform name inside a Persian sentence otherwise
      // drags its neighbouring punctuation to the wrong end.
      dir={lang === 'fa' ? 'rtl' : 'ltr'}
    >
      <Icon name={iconFor(primary.key)} size={11} className="flex-none opacity-80" />
      <span className="flex-none">{compactNumber(primary.value, lang)}</span>
      <span aria-hidden="true" className="flex-none opacity-40">
        ·
      </span>
      {/* Truncates before the number does — a cut-off count would be a wrong number, where a
          cut-off platform name is still recognisable. */}
      <span className="truncate" dir="ltr">
        {label}
      </span>
    </span>
  );
}

/**
 * The full block, for a detail screen.
 *
 * Sectioned rather than one row of numbers, because the whole point is that these came from
 * somewhere else. A reader who cannot tell our 3 downloads from Civitai's 2,913 reactions has
 * been given a misleading impression, not a dense one.
 */
export function SourceStatsBlock({ engagement }: { engagement: SourceEngagement | null }) {
  const { t, lang } = useI18n();
  if (!engagement) return null;

  return (
    <section className="flex flex-col gap-2">
      <h3 className="text-caption font-medium uppercase tracking-wide text-hint">
        {t.onSource(engagement.label)}
      </h3>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        {engagement.metrics.map((m) => (
          <span key={m.key} className="tnum flex items-center gap-1.5 text-footnote text-text">
            <Icon name={iconFor(m.key)} size={13} className="flex-none text-hint" />
            <span className="font-medium">{compactNumber(m.value, lang)}</span>
            <span className="text-hint">{metricLabel(m.key, lang)}</span>
          </span>
        ))}
      </div>
      {engagement.url && (
        <a
          href={engagement.url}
          target="_blank"
          rel="noreferrer noopener"
          className="text-caption text-accent"
          dir="ltr"
        >
          {t.viewOnSource(engagement.label)}
        </a>
      )}
    </section>
  );
}

/**
 * Our own numbers — shown only once they mean something.
 *
 * The threshold is not cosmetic. A library whose counters are all zero is a library that looks
 * unwanted, and hiding a metric until it has any signal at all is the difference between "new"
 * and "nobody took this". Returns null rather than an empty section so the surrounding layout
 * does not leave a gap where a heading would have been.
 */
export function InternalStatsBlock({
  downloadCount,
  ratingAvg,
  ratingCount,
  favoriteCount,
  viewCount,
}: {
  downloadCount: number;
  ratingAvg: number;
  ratingCount: number;
  favoriteCount: number;
  viewCount?: number;
}) {
  const { t, lang } = useI18n();

  const items: { icon: IconName; value: string; label: string }[] = [];
  if ((viewCount ?? 0) >= MEANINGFUL_COUNT)
    items.push({ icon: 'view', value: compactNumber(viewCount!, lang), label: t.views });
  if (downloadCount >= MEANINGFUL_COUNT)
    items.push({
      icon: 'download',
      value: compactNumber(downloadCount, lang),
      label: t.downloadsStat,
    });
  // A rating average means nothing without votes behind it; one 5-star vote is not "5.0".
  if (ratingCount >= MEANINGFUL_RATINGS)
    items.push({
      icon: 'star',
      value: `${ratingAvg.toFixed(1)} (${compactNumber(ratingCount, lang)})`,
      label: t.rating,
    });
  if (favoriteCount >= MEANINGFUL_COUNT)
    items.push({ icon: 'heart', value: compactNumber(favoriteCount, lang), label: t.saves });

  if (!items.length) return null;

  return (
    <section className="flex flex-col gap-2">
      <h3 className="text-caption font-medium uppercase tracking-wide text-hint">
        {t.onPromptVault}
      </h3>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        {items.map((it) => (
          <span key={it.label} className="tnum flex items-center gap-1.5 text-footnote text-text">
            <Icon name={it.icon} size={13} className="flex-none text-hint" />
            <span className="font-medium">{it.value}</span>
            <span className="text-hint">{it.label}</span>
          </span>
        ))}
      </div>
    </section>
  );
}
